import { ChipColor } from '@ui/Chip';
import { CarrierCode } from '@util/maps/carriers';
import { z } from 'zod';
import { addressSchema } from './address';
import { dateTimeSchema, uidSchema } from './shared';

export enum ReturnState {
  ReturnRequested,
  AwaitingDropoff,
  ReturnDenied,
  InTransit,
  ReturnDelivered,
  ReturnComplete,
  OnHold,
  ReturnCancelled,
}

export const ReturnStateLabels = {
  [ReturnState.ReturnRequested]: 'Return Requested',
  [ReturnState.AwaitingDropoff]: 'Awaiting Dropoff',
  [ReturnState.ReturnDenied]: 'Return Denied',
  [ReturnState.InTransit]: 'In Transit',
  [ReturnState.ReturnDelivered]: 'Return Delivered',
  [ReturnState.ReturnComplete]: 'Return Complete',
  [ReturnState.OnHold]: 'On Hold',
  [ReturnState.ReturnCancelled]: 'Return Cancelled',
} as const;

export const ReturnStateColors: Record<ReturnState, ChipColor> = {
  [ReturnState.ReturnRequested]: 'active',
  [ReturnState.AwaitingDropoff]: 'blue',
  [ReturnState.ReturnDenied]: 'error',
  [ReturnState.InTransit]: 'orange',
  [ReturnState.ReturnDelivered]: 'success',
  [ReturnState.ReturnComplete]: 'purple',
  [ReturnState.OnHold]: 'warning',
  [ReturnState.ReturnCancelled]: 'error',
} as const;

export const ReturnStateFilterOptions: {
  id: ReturnState;
  value: string;
  label: (typeof ReturnStateLabels)[keyof typeof ReturnStateLabels];
}[] = [
  {
    id: ReturnState.ReturnRequested,
    value: ReturnState.ReturnRequested.toString(),
    label: ReturnStateLabels[ReturnState.ReturnRequested],
  },
  {
    id: ReturnState.AwaitingDropoff,
    value: ReturnState.AwaitingDropoff.toString(),
    label: ReturnStateLabels[ReturnState.AwaitingDropoff],
  },
  {
    id: ReturnState.ReturnDenied,
    value: ReturnState.ReturnDenied.toString(),
    label: ReturnStateLabels[ReturnState.ReturnDenied],
  },
  {
    id: ReturnState.InTransit,
    value: ReturnState.InTransit.toString(),
    label: ReturnStateLabels[ReturnState.InTransit],
  },
  {
    id: ReturnState.ReturnDelivered,
    value: ReturnState.ReturnDelivered.toString(),
    label: ReturnStateLabels[ReturnState.ReturnDelivered],
  },
  {
    id: ReturnState.ReturnComplete,
    value: ReturnState.ReturnComplete.toString(),
    label: ReturnStateLabels[ReturnState.ReturnComplete],
  },
  {
    id: ReturnState.OnHold,
    value: ReturnState.OnHold.toString(),
    label: ReturnStateLabels[ReturnState.OnHold],
  },
  {
    id: ReturnState.ReturnCancelled,
    value: ReturnState.ReturnCancelled.toString(),
    label: ReturnStateLabels[ReturnState.ReturnCancelled],
  },
];

/**
 * Every state there is, for a support view that means "no state filter". The
 * returns queries take their states explicitly, so "all" has to be spelled out.
 */
export const ALL_RETURN_STATES: ReturnState[] = ReturnStateFilterOptions.map(
  (option) => option.id
);

export const returnSchema = z.object({
  id: z.string().min(10),
  approval_date: dateTimeSchema.optional(),
  buyer_id: uidSchema,
  carrier: z
    .string()
    .transform((x) => x.toLowerCase() as CarrierCode)
    .nullable()
    .default(null),
  chat_id: z.string().nullable().default(null),
  charge_id: z.string().optional(),
  created: dateTimeSchema,
  eligible_refund_amount: z.number().gte(0),
  /**
   * Who is producing the return label when MX Locker is not buying one: the
   * seller sending the buyer one of their own, or the buyer shipping the item
   * back themselves. Null on a return approved the usual way, where approving
   * books the label.
   */
  external_label: z.enum(['seller', 'buyer']).nullable().default(null),
  label_cost: z.number().gte(0).nullable().default(null),
  label_id: z.string().nullable().default(null),
  label_pdf: z.string().nullable().default(null),
  images: z
    .array(
      z.object({
        thumb: z.string(),
        download_url: z.string().optional(),
        full: z.string(),
      })
    )
    .default([])
    .optional(),
  is_warehouse_return: z.boolean().default(false),
  order_id: z.string(),
  product_id: z.string(),
  // how many of this product are coming back
  qty: z.number().int().gte(1).default(1),
  /**
   * What the buyer wants out of the return, and the one thing that decides
   * whether a package is involved.
   *
   * `refund` sends the items back for money. `exchange` sends them back for
   * different ones. `partial` leaves them with the buyer, so nothing ships and
   * no label is bought.
   */
  resolution: z.enum(['refund', 'exchange', 'partial']).default('refund'),
  /**
   * What the buyer asked for while keeping the items, capped by the API at what
   * sending them back would have been worth. Null on every other resolution.
   */
  partial_refund_amount: z.number().gte(0).nullable().default(null),
  ra_fee: z.number().gte(0),
  rate_id: z.string().nullable().default(null),
  restocking_fee: z.number().gte(0).nullable().default(null),
  refund_amount: z.number().gte(0).nullable().default(null),
  refund_reason: z.string().nullable().default(null),
  return_shipping_estimate: z.number().gte(0).default(0),
  service_code: z.string().nullable().default(null),
  seller_id: uidSchema,
  ship_to: addressSchema.optional(), // added when approved
  // id of the return whose label covers the whole box; set on every return
  // travelling in it, including the one that owns the label
  shipping_group_id: z.string().nullable().default(null),
  state: z.nativeEnum(ReturnState).default(ReturnState.ReturnRequested),
  tracking_number: z.string().nullable().default(null),
  final_amount: z.number().gte(0).nullable().default(null),
  // warehouse specific fields
  relisted_product_id: z.string().optional(),
  received_by_warehouse: z.enum(['yes', 'needs_attention']).optional(), // yes means the item was received in good condition, needs_attention means the item was not received in good condition
  warehouse_comments: z.string().optional(),
  // admin specific fields
  notes: z
    .array(
      z.object({
        text: z.string(),
        // markup form of `text`; an <h2> marks the fragment to call out
        text_html: z.string().optional(),
        created: z.number().optional().default(Date.now()),
        // author of the note; absent on notes written by code
        uid: uidSchema.optional(),
      })
    )
    .optional(),
});

export interface ReturnInput extends z.input<typeof returnSchema> {}
export interface ReturnDocument extends z.output<typeof returnSchema> {}

export type ExternalLabelProvider = NonNullable<
  ReturnDocument['external_label']
>;

/** The ways a return can be approved without MX Locker buying the label. */
export const EXTERNAL_LABEL_OPTIONS: {
  value: ExternalLabelProvider;
  label: string;
}[] = [
  { value: 'seller', label: 'Seller sends the buyer their own label' },
  { value: 'buyer', label: 'Buyer ships with their own label' },
];

/**
 * How many of the product are coming back.
 *
 * Returns are read straight out of Firestore and cast rather than parsed, so
 * the schema default never reaches them: anything written before quantities
 * existed carries no `qty` at all. Multiplying by that undefined turns whole
 * totals into NaN, so every read goes through here.
 */
export const returnQty = (ret: Pick<ReturnDocument, 'qty'>) => ret.qty || 1;

/**
 * States where a return has let go of the units it was opened for. Nothing was
 * ever taken off the buyer, so those units can be asked for again.
 */
const RELEASED_RETURN_STATES: ReturnState[] = [
  ReturnState.ReturnDenied,
  ReturnState.ReturnCancelled,
];

/**
 * How many of an order item are still with the buyer to send back: what they
 * bought minus what live returns already account for.
 *
 * A line item is one row however many units it holds, so returning some of it
 * leaves the rest returnable. Reading the row alone would either offer units
 * that are already on their way back or refuse the ones that never left.
 */
export const remainingReturnableQty = (
  item: { id: string; qty: number },
  returns: ReturnDocument[] | null | undefined
) => {
  const claimed = (returns ?? [])
    .filter(
      (ret) =>
        ret.product_id === item.id &&
        !RELEASED_RETURN_STATES.includes(ret.state)
    )
    .reduce((sum, ret) => sum + returnQty(ret), 0);
  return Math.max((item.qty || 1) - claimed, 0);
};
